import { radarSourceEnum, toKnownSource } from "@modules/radar/radar.types";
import type { RadarSourceKey } from "@modules/radar/radar.types";
import type { FindOneContact200 } from "@shared/generated/radar/models/contactsController/FindOneContact";

const KNOWN_SOURCE_ORDER: string[] = Object.values(radarSourceEnum);

export type RadarSignalEventViewModel = {
  id: string;
  description: string;
  occurredAt: string | null;
};

export type RadarSignalSourceGroupViewModel = {
  source: RadarSourceKey | undefined;
  label: string;
  events: RadarSignalEventViewModel[];
};

export type RadarSignalsSummaryViewModel = {
  score: number | null;
  summary: string | null;
  insights: string[];
  suggestedApproach: string | null;
  executedAt: string | null;
};

function stripSurroundingQuotes(text: string): string {
  return text.replace(/^["'“‘]+/, "").replace(/["'”’]+$/, "");
}

type RadarContactSignalsViewModel = {
  totalSignals: number;
  sourceCount: number;
  sources: RadarSignalSourceGroupViewModel[];
  summary: RadarSignalsSummaryViewModel | null;
};

export function toRadarContactSignalsViewModel(
  contact: FindOneContact200,
): RadarContactSignalsViewModel {
  // firstSeenAt is when Radar discovered the event, not when it happened —
  // only usable to order events, never as a stand-in for their actual date.
  const eventsByRawSource = new Map<
    string,
    { event: RadarSignalEventViewModel; sortKey: string }[]
  >();

  for (const event of contact.events) {
    const viewModelEvent: RadarSignalEventViewModel = {
      id: event.id,
      description: event.description,
      occurredAt: event.eventDate,
    };
    const entry = {
      event: viewModelEvent,
      sortKey: event.eventDate ?? event.firstSeenAt,
    };
    const group = eventsByRawSource.get(event.source);
    if (group) {
      group.push(entry);
    } else {
      eventsByRawSource.set(event.source, [entry]);
    }
  }

  for (const group of eventsByRawSource.values()) {
    group.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  }

  const rawSources = [...eventsByRawSource.keys()];
  const orderedRawSources = [
    ...KNOWN_SOURCE_ORDER.filter((known) => rawSources.includes(known)),
    ...rawSources.filter((raw) => !KNOWN_SOURCE_ORDER.includes(raw)),
  ];

  const sources: RadarSignalSourceGroupViewModel[] = orderedRawSources.map(
    (rawSource) => ({
      source: toKnownSource(rawSource),
      label: rawSource,
      events: (eventsByRawSource.get(rawSource) ?? []).map(
        (entry) => entry.event,
      ),
    }),
  );

  const summary = contact.lastExecution
    ? {
        score: contact.lastExecution.priorityScore,
        summary: contact.lastExecution.summary,
        insights: contact.lastExecution.insights ?? [],
        suggestedApproach: contact.lastExecution.suggestedApproach
          ? stripSurroundingQuotes(contact.lastExecution.suggestedApproach)
          : null,
        executedAt: contact.lastExecution.executedAt,
      }
    : null;

  return {
    totalSignals: contact.events.length,
    sourceCount: sources.length,
    sources,
    summary,
  };
}
